import { Fragment, useMemo, useState } from 'react';
import { ChevronDown, TriangleAlert } from 'lucide-react';
import type {
  AlertVisibility,
  BulkAlertSelection,
  GroupedAlert,
} from '@kirin/api-client/contracts/alerts';
import {
  severityOptionsDescending,
  type Severity,
} from '@kirin/api-client/contracts/shared/severity';
import type { BulkToolbarContext } from '@/components/DataTable/types';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SUPPORT_READONLY_TOOLTIP } from '@/components/alertActionTooltipCopy';
import { useToast } from '@/hooks/use-toast';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { trackEvent } from '@/lib/analytics';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  useBulkAlertAction,
  useBulkAlertEligibility,
  type BulkAlertScope,
} from '@/hooks/api/useBulkAlertActions';
import {
  BULK_SEVERITY_FAILURE_TOAST,
  BULK_SEVERITY_RESET_FAILURE_TOAST,
  OVERRIDE_SEVERITY_LABEL,
  RESET_SEVERITY_LABEL,
  bulkActionFailureToast,
  bulkActionSuccessToast,
  bulkSeverityResetSuccessToast,
  bulkSeveritySuccessToast,
  exclusionActions,
  modalCountsForAction,
  muteUnmuteActions,
  selectionSummary,
  selectAllAcrossPagesLabel,
  selectedAllMatchingSummary,
  type BulkBarAction,
  type BulkSuppressionAction,
} from './bulkActionCopy';
import { BulkActionConfirmDialog } from './BulkActionConfirmDialog';
import type { BulkActionDuration } from './BulkActionConfirmDialog';
import {
  BulkSeverityConfirmDialog,
  type BulkSeverityDialogTarget,
} from './BulkSeverityConfirmDialog';

export interface AlertsBulkActionBarProps {
  ctx: BulkToolbarContext<GroupedAlert>;
  /**
   * Scope the bulk action targets — an organization (manager/owner) or the
   * authenticated user's own alerts. Drives both the eligibility query and the
   * apply mutation, so the same bar serves the org and personal Alerts screens.
   */
  scope: BulkAlertScope;
  /** Current view's suppression selector — scopes "select all across pages". */
  visibility: AlertVisibility;
  /**
   * Render the apply-action buttons disabled (support is read-only — KIR-4128).
   * Selection itself stays interactive: support may select rows and read the
   * eligibility summary, it just can't apply an action. "Disable, don't hide",
   * matching the per-row action buttons.
   */
  disabled?: boolean;
}

/**
 * Bulk-action bar for the Alerts screen (KIR-3252). Rendered in the DataTable's
 * toolbar slot when ≥1 row is selected. Shows the selection summary + the
 * cross-page "select all" affordance, derives state-aware action buttons from
 * the eligibility preview, and applies an all-or-nothing action with a single
 * success/failure toast (no undo). With `alert_severity_override` on it also
 * offers "Override Severity" (KIR-5192), confirmed against the server-resolved
 * `eligibility.totalSelected`, and "Reset to Default" when the preview counts
 * at least one overridden alert.
 */
export function AlertsBulkActionBar({
  ctx,
  scope,
  visibility,
  disabled = false,
}: AlertsBulkActionBarProps): React.ReactElement | null {
  const { toast } = useToast();
  const [activeModal, setActiveModal] = useState<{
    action: BulkSuppressionAction;
    eligibleCount: number;
    alreadyCount: number;
  } | null>(null);
  const [severityModal, setSeverityModal] =
    useState<BulkSeverityDialogTarget | null>(null);

  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ALERT_SEVERITY_OVERRIDE,
  ]);
  const severityOverrideEnabled =
    flags[flagsmithFeatureFlags.ALERT_SEVERITY_OVERRIDE] === true;

  // Treat an active search term as a filter for copy purposes: the ticket's
  // copy rule says "matching filter/s" and doesn't carve out search, so a search
  // narrows the list the same way a column filter does. Count it as one filter.
  const hasSearch = ctx.search.trim().length > 0;
  const filterCount = ctx.filters.length + (hasSearch ? 1 : 0);
  const selectAll = ctx.selectAllAcrossPages;
  const excludedCount = ctx.excludedRowIds.length;
  // In select-all mode the effective selection is (total − excluded).
  const effectiveCount = selectAll
    ? Math.max(0, ctx.total - excludedCount)
    : ctx.selectedRowIds.length;

  const selection: BulkAlertSelection = useMemo(
    () =>
      selectAll
        ? {
            mode: 'query',
            filters: ctx.filters,
            search: ctx.search || undefined,
            // Scope "select all" to the current view so it matches the list.
            visibility,
            // "all matching EXCEPT these" — the user's deselections.
            ...(ctx.excludedRowIds.length > 0
              ? { excludeKeys: ctx.excludedRowIds }
              : {}),
          }
        : { mode: 'keys', alertKeys: ctx.selectedRowIds },
    [
      selectAll,
      ctx.filters,
      ctx.search,
      ctx.excludedRowIds,
      ctx.selectedRowIds,
      visibility,
    ]
  );

  // Coalesce rapid checkbox clicks into one eligibility request.
  const debouncedSelection = useDebouncedValue(selection, 300);
  const eligibilityEnabled = effectiveCount > 0;
  const { data: eligibility, isFetching: eligibilityFetching } =
    useBulkAlertEligibility(scope, debouncedSelection, {
      enabled: eligibilityEnabled,
    });

  // The bar's labels/counts come from the eligibility of `debouncedSelection`,
  // but an action applies the LIVE `selection`. While the debounce is pending or
  // the preview is refetching they describe different sets (placeholderData even
  // keeps the prior counts) — so actions stay disabled until the preview matches
  // the live selection, guaranteeing the modal/confirm counts equal what's applied.
  const eligibilityPending =
    debouncedSelection !== selection || eligibilityFetching;

  const applyMutation = useBulkAlertAction(scope);

  const muteActions: BulkBarAction[] = eligibility
    ? muteUnmuteActions(eligibility)
    : [];
  const exclusion = eligibility
    ? exclusionActions(eligibility)
    : { actions: [] as BulkBarAction[], ineligibleNote: undefined };
  const actionButtons = [...muteActions, ...exclusion.actions];

  const showSelectAllAcrossPages =
    !selectAll &&
    ctx.selectedRowIds.length > 0 &&
    ctx.isAllPageSelected &&
    ctx.pageCount > 1 &&
    ctx.total > ctx.selectedRowIds.length;

  // Keep the bar mounted while in select-all mode (even at 0 effective) so the
  // user always has a "Clear Selection" escape.
  if (!selectAll && ctx.selectedRowIds.length === 0) {
    return null;
  }

  // A select-all with some alerts excluded reads like a plain selection.
  const summary =
    selectAll && excludedCount === 0
      ? selectedAllMatchingSummary({ total: ctx.total, filterCount })
      : selectionSummary({ count: effectiveCount, filterCount });

  const openActionModal = (action: BulkSuppressionAction): void => {
    // Don't open with counts that don't match the live selection yet.
    if (!eligibility || eligibilityPending) return;
    setActiveModal({ action, ...modalCountsForAction(action, eligibility) });
  };

  // Same guard as openActionModal: the confirm count must be the server's
  // count for the live selection.
  const openSeverityModal = (severity: Severity): void => {
    if (!eligibility || eligibilityPending) return;
    setSeverityModal({ severity, totalSelected: eligibility.totalSelected });
  };

  const openResetSeverityModal = (): void => {
    if (!eligibility || eligibilityPending) return;
    setSeverityModal({
      severity: 'reset',
      ...modalCountsForAction('reset-severity', eligibility),
    });
  };

  // Always offered, disabled while nothing in the selection is overridden. A
  // missing bucket (flag off on the server, or an older backend) hides it.
  const resetSeverityBucket = eligibility?.eligibleForResetSeverity;
  const showResetSeverity = resetSeverityBucket !== undefined;
  const resetSeverityDisabled = (resetSeverityBucket ?? 0) === 0;

  const severityTriggerDisabled =
    applyMutation.isPending ||
    eligibilityPending ||
    !eligibility ||
    eligibility.totalSelected === 0;

  const confirmResetSeverity = (): void => {
    applyMutation.mutate(
      { action: 'reset-severity', selection },
      {
        onSuccess: result => {
          const notOverriddenCount = result.notOverriddenCount ?? 0;
          toast(
            bulkSeverityResetSuccessToast({
              changedCount: result.changedCount,
              notOverriddenCount,
            })
          );
          trackEvent('alert_severity_bulk_reset', {
            changed_count: result.changedCount,
            not_overridden_count: notOverriddenCount,
            selection_mode: selection.mode,
            scope: scope.kind,
          });
          setSeverityModal(null);
        },
        onError: () => {
          toast({
            title: BULK_SEVERITY_RESET_FAILURE_TOAST,
            variant: 'destructive',
          });
        },
      }
    );
  };

  const confirmSeverity = (): void => {
    if (!severityModal) return;
    if (severityModal.severity === 'reset') {
      confirmResetSeverity();
      return;
    }
    const { severity } = severityModal;
    applyMutation.mutate(
      { action: 'override-severity', selection, severity },
      {
        onSuccess: result => {
          const alreadyOverriddenCount = result.alreadyOverriddenCount ?? 0;
          toast(
            bulkSeveritySuccessToast(severity, {
              changedCount: result.changedCount,
              alreadyOverriddenCount,
            })
          );
          trackEvent('alert_severity_bulk_overridden', {
            severity,
            changed_count: result.changedCount,
            already_overridden_count: alreadyOverriddenCount,
            selection_mode: selection.mode,
            scope: scope.kind,
          });
          setSeverityModal(null);
        },
        onError: () => {
          toast({ title: BULK_SEVERITY_FAILURE_TOAST, variant: 'destructive' });
        },
      }
    );
  };

  const confirmAction = (duration?: BulkActionDuration): void => {
    if (!activeModal) return;
    const { action } = activeModal;
    applyMutation.mutate(
      {
        action,
        selection,
        mutedUntil: duration?.mutedUntil,
        excludedUntil: duration?.excludedUntil,
      },
      {
        onSuccess: result => {
          toast({
            title: bulkActionSuccessToast(action, result.changedCount),
          });
          setActiveModal(null);
        },
        onError: () => {
          toast({
            title: bulkActionFailureToast(action),
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <div className="rounded-lg border border-[#dde6f7] bg-[#eef2fb] px-4 py-1 text-sm dark:border-[#1e2a44] dark:bg-[#11192e]">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium">{summary}</span>

        {showSelectAllAcrossPages && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 font-medium underline underline-offset-2"
            onClick={ctx.enterSelectAllAcrossPages}
          >
            {selectAllAcrossPagesLabel({ total: ctx.total, filterCount })}
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          className="ml-1 h-7"
          onClick={ctx.clearSelection}
        >
          Clear Selection
        </Button>

        {actionButtons.map(btn => (
          <Fragment key={btn.action}>
            <span aria-hidden="true" className="text-muted-foreground">
              ·
            </span>
            {disabled ? (
              // Support read-only: a visually-disabled trigger inside a
              // focusable <span>, not a native `disabled` button. A disabled
              // button gets `pointer-events-none` and swallows hover/focus, so
              // the explanatory tooltip would never open. Mirrors the
              // AlertAddToListButton / AlertExcludeButton non-interactive branch.
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="inline-flex">
                    <Button
                      variant="outline"
                      size="sm"
                      className="pointer-events-none h-7 opacity-50"
                      aria-disabled="true"
                      onClick={event => event.preventDefault()}
                    >
                      {btn.label}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{SUPPORT_READONLY_TOOLTIP}</TooltipContent>
              </Tooltip>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-7"
                disabled={applyMutation.isPending || eligibilityPending}
                onClick={() => openActionModal(btn.action)}
              >
                {btn.label}
              </Button>
            )}
          </Fragment>
        ))}

        {/* Right after the exclusion buttons (the last action buttons), in a
            tooltip so the bar stays one line (KIR-5379). */}
        {exclusion.ineligibleNote && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={exclusion.ineligibleNote}
                className="inline-flex size-7 items-center justify-center text-status-warn-emphasis"
              >
                <TriangleAlert className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{exclusion.ineligibleNote}</TooltipContent>
          </Tooltip>
        )}

        {severityOverrideEnabled && (
          <>
            <span aria-hidden="true" className="text-muted-foreground">
              ·
            </span>
            {disabled ? (
              // Support read-only — same non-interactive branch as above.
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="inline-flex">
                    <Button
                      variant="outline"
                      size="sm"
                      className="pointer-events-none h-7 opacity-50"
                      aria-disabled="true"
                      onClick={event => event.preventDefault()}
                    >
                      {OVERRIDE_SEVERITY_LABEL}
                      <ChevronDown className="size-4" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{SUPPORT_READONLY_TOOLTIP}</TooltipContent>
              </Tooltip>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={severityTriggerDisabled}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7"
                    disabled={severityTriggerDisabled}
                  >
                    {OVERRIDE_SEVERITY_LABEL}
                    <ChevronDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {severityOptionsDescending.map(option => (
                    <DropdownMenuItem
                      key={option.value}
                      onSelect={() => openSeverityModal(option.value)}
                    >
                      <SeverityBadge riskLevel={option.value} showIcon />
                    </DropdownMenuItem>
                  ))}
                  {showResetSeverity && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        disabled={resetSeverityDisabled}
                        onSelect={openResetSeverityModal}
                      >
                        {RESET_SEVERITY_LABEL}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        )}
      </div>

      {activeModal && (
        <BulkActionConfirmDialog
          open
          onOpenChange={open => {
            if (!open) setActiveModal(null);
          }}
          action={activeModal.action}
          eligibleCount={activeModal.eligibleCount}
          alreadyCount={activeModal.alreadyCount}
          isPending={applyMutation.isPending}
          onConfirm={confirmAction}
        />
      )}

      {severityModal && (
        <BulkSeverityConfirmDialog
          open
          onOpenChange={open => {
            if (!open) setSeverityModal(null);
          }}
          {...severityModal}
          isPending={applyMutation.isPending}
          onConfirm={confirmSeverity}
        />
      )}
    </div>
  );
}

AlertsBulkActionBar.displayName = 'AlertsBulkActionBar';
